<template>
  <div>
    <Teleport to="#modals">
      <Transition name="modal-fade" appear>
        <component
          v-if="modal.show.value"
          :key="modalKey"
          :is="modal.component.value"
          v-bind="modal.props.value"
          @close="handleClose"
        />
      </Transition>
    </Teleport>

    <button
      v-if="appearance === 'bar'"
      class="search-modal-button search-modal-button--bar"
      title="Open Search"
      type="button"
      @click="openConfirm"
      @mouseenter="preloadModal"
      @focus="preloadModal"
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" aria-hidden="true" focusable="false">
        <path fill="currentColor" d="M416 208c0 45.9-14.9 88.3-40 122.7L502.6 457.4c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L330.7 376c-34.4 25.2-76.8 40-122.7 40C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208zM208 352a144 144 0 1 0 0-288 144 144 0 1 0 0 288z"/>
      </svg>
      <span class="search-modal-button__placeholder">Search</span>
    </button>

    <button
      v-else
      class="svg-button svg-button--search-modal search-modal-button"
      title="Open Search"
      type="button"
      @click="openConfirm"
      @mouseenter="preloadModal"
      @focus="preloadModal"
    >
    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 54 54">
      <circle cx="27" cy="27" r="27" fill="currentColor"/>
      <path fill="#fff" stroke="#fff" d="M15.24 15.24c4.32-4.32 11.33-4.32 15.65 0 3.57 3.57 4.27 9.1 1.7 13.44l7.63 7.63a2.752 2.752 0 0 1 0 3.9 2.752 2.752 0 0 1-3.9 0l-7.63-7.63c-5.26 3.1-12.05 1.35-15.15-3.91a11.074 11.074 0 0 1 1.7-13.44m9.72.76c-1.88-.5-3.88-.24-5.56.73h-.02a7.372 7.372 0 0 0-3.39 4.45c-.06.23-.3.37-.53.3s-.37-.3-.3-.53a8.19 8.19 0 0 1 3.79-4.96h.02a8.09 8.09 0 0 1 6.21-.83c.23.06.37.3.31.53-.06.23-.3.37-.53.31m11.97 23.62c.78.7 1.98.63 2.68-.16.64-.72.64-1.81 0-2.53l-7.5-7.5c-.74 1.04-1.64 1.95-2.68 2.68l7.5 7.5ZM12.86 23.06c0 5.63 4.57 10.2 10.2 10.2s10.2-4.57 10.2-10.2-4.57-10.2-10.2-10.2c-2.7 0-5.3 1.07-7.21 2.99a10.17 10.17 0 0 0-2.99 7.21"/>
    </svg>
    </button>
  </div>
</template>

<script setup>
import { markRaw, onMounted, ref } from 'vue';
import { useModal } from '../composables/useModal.ts';

const modal = useModal();
const modalKey = ref(0);

const props = defineProps({
  rootId: {
    type: Number,
    required: true
  },
  // 'icon' = circled magnifier button, 'bar' = fake search-input trigger
  appearance: {
    type: String,
    default: 'icon'
  },
  filters: {
    type: Array,
    default: () => []
  },
  pageTypes: {
    type: Array,
    default: () => []
  }
});

/**
 * Helpers for reading URL state
 */
const getSearchParams = () => {
  try {
    const url = new URL(window.location.href);
    return {
      open: url.searchParams.get('searchOpen') === '1',
      term: url.searchParams.get('search') || ''
    };
  } catch {
    return { open: false, term: '' };
  }
};

/**
 * Async loader for the modal component
 */
let preloadPromise = null;

const loadModal = () => {
  if (!preloadPromise) {
    preloadPromise = import(
      /* webpackChunkName: 'SearchModal', webpackPrefetch: true */
      '../components/SearchModal.vue'
    );
  }
  return preloadPromise;
};

const preloadModal = () => {
  // fire and forget, just to warm the chunk
  void loadModal();
};

/**
 * Open the modal (normal click)
 */
const openConfirm = async () => {
  const { term } = getSearchParams();
  const module = await loadModal();
  const SearchModal = module.default || module;

  modalKey.value++;
  modal.component.value = markRaw(SearchModal);
  modal.showModal({
    rootId: props.rootId,
    initialSearchTerm: term,
    filters: props.filters,
    pageTypes: props.pageTypes
  });
};

/**
 * Close handler from the modal
 * (SearchModal itself already clears query params & state)
 */
const handleClose = () => {
  modal.hideModal();
};

/**
 * Auto-open on page load when coming back via Back button
 * and URL has searchOpen=1&search=...
 */
onMounted(async () => {
  const { open, term } = getSearchParams();

  if (!open || !term) {
    return;
  }

  const module = await loadModal();
  const SearchModal = module.default || module;

  modalKey.value++;
  modal.component.value = markRaw(SearchModal);
  modal.showModal({
    rootId: props.rootId,
    initialSearchTerm: term,
    filters: props.filters,
    pageTypes: props.pageTypes
  });
});
</script>